/**
 * Actuator Service - Komunikace s API pro ovladani aktuatoru
 * Pouziva MQTT pres backend pro komunikaci s ESP32
 */

import { apiClient } from './api';
import {
  Actuator,
  ActuatorCommand,
  BulkActuatorCommand,
  ActuatorConfig,
  ActuatorState,
  TestModeState,
} from '../types/actuators';

// Cache pro stavy aktuatoru (aktualizovano pres WebSocket)
const actuatorStatesCache: Map<string, Actuator> = new Map();
let selectedDeviceId: string | null = null;

/**
 * Prevede chybu z API na lidskou hlasku, kterou lze ukazat uzivateli.
 *
 * Proc to existuje: prikazy na hardware drive chyby POLYKALY (console.error +
 * return false). Kdyz backend prikaz zamitl — treba kvuli bezpecnostni pojistce,
 * opravnenim nebo nedostupnemu zarizeni — uzivatel nedostal zadnou zpetnou vazbu
 * a prepinac se pritom optimisticky prepnul. Tykalo se to i nouzoveho zastaveni.
 * CLAUDE.md zaroven zakazuje ukazovat surovy error z API, proto prekladame.
 */
function actuatorError(error: unknown, fallback: string): Error {
  const response = (error as { response?: { status?: number; data?: Record<string, unknown> } })?.response;
  const status = response?.status;
  const serverMessage = String(response?.data?.['message'] ?? response?.data?.['error'] ?? '');

  if (!response) {
    return new Error('Server neodpovídá. Zkontrolujte připojení a zkuste to znovu.');
  }
  if (serverMessage.toLowerCase().includes('interlock')) {
    return new Error('Akci zablokovala bezpečnostní pojistka — otevřete nejdřív potřebný ventil.');
  }
  if (status === 403) {
    return new Error('K této akci nemáte oprávnění.');
  }
  if (status === 409) {
    return new Error('Akci teď nelze provést — zařízení je v nekompatibilním stavu.');
  }
  if (status === 502 || status === 503) {
    return new Error('Zařízení není dostupné. Zkontrolujte, že je online, a zkuste to znovu.');
  }
  return new Error(fallback);
}

// Pozn.: driv tu byla funkce initializeActuators(), ktera z DEFAULT_ACTUATOR_MAP
// vyrobila seznam 16 aktuatoru a pouzivala se jako fallback pri vypadku API.
// Odstranena zamerne — vypadek se ma priznat, ne zamaskovat panelem, ktery
// vypada funkcne, ale nic neovlada.

/**
 * Mapovani backend kategorii na frontend typy
 * Backend: pumps, valves, climate, lighting, mixing, other
 * Frontend: pump, valve, heater, light, mixer, other
 */
const mapCategory = (backendCategory: string): string => {
  const categoryMap: Record<string, string> = {
    pumps: 'pump',
    valves: 'valve',
    climate: 'heater',
    lighting: 'light',
    mixing: 'mixer',
    other: 'other',
  };
  return categoryMap[backendCategory] || backendCategory;
};

export const actuatorService = {
  /**
   * Ziskani vsech aktuatoru pro zarizeni
   */
  getActuators: async (deviceId: string): Promise<Actuator[]> => {
    try {
      // Pokus o ziskani z API
      const response = await apiClient.get<{ data: any[]; count: number }>(
        `/devices/${deviceId}/actuators`,
      );

      const rawActuators = response.data;
      if (rawActuators && rawActuators.length > 0) {
        // Mapovani backend formatu na frontend Actuator typ
        const actuators: Actuator[] = rawActuators.map((a: any) => ({
          ...a,
          // Backend vraci state jako boolean, frontend ocekava 'ON' | 'OFF' | 'UNKNOWN'
          state: a.state === true ? 'ON' : a.state === false ? 'OFF' : (a.state || 'UNKNOWN'),
          // Mapovani backend kategorii na frontend
          category: mapCategory(a.category),
          lastStateChange: a.lastChanged ? new Date(a.lastChanged) : undefined,
        }));

        // Aktualizace cache
        actuators.forEach((a) => {
          actuatorStatesCache.set(a.id, a);
        });
        selectedDeviceId = deviceId;
        return actuators;
      }
    } catch (error) {
      console.warn('Nepodarilo se nacist aktuatory z API:', error);
      // Chybu propoustime dal, aby React Query zobrazil chybovy stav.
      //
      // Driv se tu misto toho vratila DEFAULT_ACTUATOR_MAP: pri vypadku API
      // uzivatel videl normalne vypadajici panel 16 aktuatoru a netusil, ze
      // nekomunikuje se zarizenim. U ovladani cerpadel a topeni je to
      // nebezpecne — panel vypada funkcne, ale prepinace nic neovladaji.
      throw actuatorError(error, 'Stav aktuátorů se nepodařilo načíst. Zkontrolujte připojení.');
    }

    // API odpovedelo, ale seznam je prazdny — zarizeni jeste nic nenahlasilo.
    // Vracime prazdne pole (UI ukaze prazdny stav), NE vymyslenou mapu.
    selectedDeviceId = deviceId;
    return [];
  },

  /**
   * Ziskani jednoho aktuatoru
   */
  getActuator: async (deviceId: string, actuatorId: string): Promise<Actuator | null> => {
    // Nejprve zkus cache
    if (selectedDeviceId === deviceId && actuatorStatesCache.has(actuatorId)) {
      return actuatorStatesCache.get(actuatorId) || null;
    }

    try {
      // Backend obaluje detail do { data: ... } — stejne jako seznam aktuatoru vyse.
      // Driv se tu telo vracelo rovnou jako Actuator, takze VSECHNA pole byla
      // undefined: stranky konfigurace a historie se pri primem otevreni nenacetly
      // a na kartach svitilo "undefined".
      const response = await apiClient.get<{ data: Actuator }>(
        `/devices/${deviceId}/actuators/${actuatorId}`,
      );
      return response?.data ?? null;
    } catch (error) {
      console.error('Chyba pri ziskavani aktuatoru:', error);
      return null;
    }
  },

  /**
   * Odeslani prikazu pro aktuator (ON/OFF s volitelnou hodnotou PWM)
   */
  sendCommand: async (command: ActuatorCommand): Promise<boolean> => {
    try {
      const payload: Record<string, any> = {
        state: command.state,
        timestamp: command.timestamp || new Date().toISOString(),
      };

      // Add optional value for PWM actuators (0-100%)
      if (command.value !== undefined) {
        payload['value'] = command.value;
      }

      // Add optional duration for timed operations
      if (command.duration !== undefined) {
        payload['duration'] = command.duration;
      }

      await apiClient.post<{ success: boolean }>(
        `/devices/${command.deviceId}/actuators/${command.actuatorId}/command`,
        payload,
      );

      // Optimisticka aktualizace cache
      const cached = actuatorStatesCache.get(command.actuatorId);
      if (cached) {
        cached.state = command.state;
        cached.lastStateChange = new Date();
        actuatorStatesCache.set(command.actuatorId, cached);
      }

      return true;
    } catch (error) {
      console.error('Chyba pri odesilani prikazu:', error);
      // Chybu propoustime dal — mutace v useActuators ji zachyti v onError a ukaze
      // uzivateli. Drive se tu vracelo false a selhani prikazu na cerpadlo nebo
      // topeni probehlo uplne potichu.
      throw actuatorError(error, 'Příkaz se nepodařilo odeslat. Zkuste to znovu.');
    }
  },

  /**
   * Spusti cerpadlo pro kalibraci pruchodnosti — bypassuje interlocky na backendu.
   * Pouzivej pouze z kalibraci pumpy, ne z bezneho ovladani.
   */
  calibrateRun: async (
    deviceId: string,
    actuatorId: string,
    pwm: number,
    duration_ms?: number,
  ): Promise<void> => {
    await apiClient.post<{ success: boolean }>(
      `/devices/${deviceId}/actuators/${actuatorId}/calibrate-run`,
      { pwm, ...(duration_ms !== undefined ? { duration_ms } : {}) },
    );
  },

  /**
   * Zapnuti aktuatoru
   */
  turnOn: async (deviceId: string, actuatorId: string): Promise<boolean> => {
    return actuatorService.sendCommand({
      deviceId,
      actuatorId,
      state: 'ON',
    });
  },

  /**
   * Vypnuti aktuatoru
   */
  turnOff: async (deviceId: string, actuatorId: string): Promise<boolean> => {
    return actuatorService.sendCommand({
      deviceId,
      actuatorId,
      state: 'OFF',
    });
  },

  /**
   * Nastaveni hodnoty (PWM 0-100%) pro dimmable aktuatory (svetla)
   */
  setValue: async (deviceId: string, actuatorId: string, value: number): Promise<boolean> => {
    return actuatorService.sendCommand({
      deviceId,
      actuatorId,
      state: value > 0 ? 'ON' : 'OFF',
      value: Math.max(0, Math.min(100, value)),
    });
  },

  /**
   * Prepnuti stavu aktuatoru
   */
  toggle: async (deviceId: string, actuatorId: string): Promise<boolean> => {
    const current = actuatorStatesCache.get(actuatorId);
    const newState = current?.state === 'ON' ? 'OFF' : 'ON';
    return actuatorService.sendCommand({
      deviceId,
      actuatorId,
      state: newState,
    });
  },

  /**
   * Central Stop - vypne vsechny aktuatory
   */
  emergencyStop: async (deviceId: string): Promise<boolean> => {
    try {
      await apiClient.post<{ success: boolean }>(`/devices/${deviceId}/actuators/emergency-stop`, {
        reason: 'emergency_stop',
        timestamp: new Date().toISOString(),
      });

      // Aktualizace cache - vse vypnout
      actuatorStatesCache.forEach((actuator, id) => {
        actuator.state = 'OFF';
        actuator.lastStateChange = new Date();
        actuatorStatesCache.set(id, actuator);
      });

      return true;
    } catch (error) {
      console.error('Chyba pri nouzovem zastaveni:', error);
      // U nouzoveho zastaveni je tiche selhani nejnebezpecnejsi ze vsech —
      // uzivatel musi vedet, ze aktory NEJSOU vypnute.
      throw actuatorError(
        error,
        'Nouzové zastavení se nepodařilo odeslat — aktory nemusí být vypnuté! Zkuste to znovu.',
      );
    }
  },

  /**
   * Zrusit Central Stop - odblokuje vsechny aktuatory
   */
  resetEmergencyStop: async (deviceId: string): Promise<boolean> => {
    try {
      await apiClient.post<{ success: boolean }>(`/devices/${deviceId}/actuators/emergency-reset`, {
        reason: 'manual_reset',
        timestamp: new Date().toISOString(),
      });
      return true;
    } catch (error) {
      console.error('Chyba pri ruseni nouzoveho zastaveni:', error);
      throw actuatorError(error, 'Nouzový stav se nepodařilo zrušit. Zkuste to znovu.');
    }
  },

  /**
   * Hromadny prikaz
   */
  bulkCommand: async (command: BulkActuatorCommand): Promise<boolean> => {
    try {
      await apiClient.post<{ success: boolean }>(`/devices/${command.deviceId}/actuators/bulk`, {
        state: command.state,
        actuatorIds: command.actuatorIds,
        reason: command.reason || 'manual',
        timestamp: new Date().toISOString(),
      });

      // Aktualizace cache
      const idsToUpdate = command.actuatorIds || Array.from(actuatorStatesCache.keys());
      idsToUpdate.forEach((id) => {
        const actuator = actuatorStatesCache.get(id);
        if (actuator) {
          actuator.state = command.state;
          actuator.lastStateChange = new Date();
          actuatorStatesCache.set(id, actuator);
        }
      });

      return true;
    } catch (error) {
      console.error('Chyba pri hromadnem prikazu:', error);
      return false;
    }
  },

  /**
   * Spusteni test mode - sekvencni test vsech aktuatoru
   */
  startTestMode: async (deviceId: string, options?: { actuatorIds?: string[] }): Promise<TestModeState | null> => {
    try {
      const response = await apiClient.post<any>(
        `/devices/${deviceId}/actuators/test-mode/start`,
        options || {},
      );
      
      if (response && response.id) {
        return {
          ...response,
          sessionId: response.id, // Frontend uses sessionId, backend returns id
        };
      }
      
      if (response && response.data && response.data.id) {
        return {
          ...response.data,
          sessionId: response.data.id,
        };
      }
      
      return null;
    } catch (error) {
      console.error('Chyba pri spusteni test mode:', error);
      return null;
    }
  },

  /**
   * Ziskani stavu test mode session
   */
  getTestModeStatus: async (deviceId: string, sessionId: string): Promise<TestModeState | null> => {
    try {
      const response = await apiClient.get<any>(
        `/devices/${deviceId}/actuators/test-mode/${sessionId}`
      );
      if (response && response.id) {
        return {
          ...response,
          sessionId: response.id,
        };
      }
      // Keep backward compatibility if it still wraps
      if (response && response.data && response.data.id) {
        return {
          ...response.data,
          sessionId: response.data.id,
        }
      }
      return null;
    } catch (error) {
      console.error('Chyba pri ziskani stavu test mode:', error);
      return null;
    }
  },

  /**
   * Zastaveni test mode
   */
  stopTestMode: async (deviceId: string, sessionId?: string): Promise<boolean> => {
    if (!sessionId) {
      console.warn('Cannot stop test mode without sessionId');
      return false;
    }
    try {
      await apiClient.post<{ success: boolean }>(
        `/devices/${deviceId}/actuators/test-mode/${sessionId}/stop`,
        {},
      );
      return true;
    } catch (error) {
      console.error('Chyba pri zastaveni test mode:', error);
      return false;
    }
  },

  /**
   * Potvrzeni vysledku testu aktuatoru
   */
  acknowledgeTestResult: async (deviceId: string, actuatorId: string): Promise<boolean> => {
    try {
      await apiClient.post<{ success: boolean }>(
        `/devices/${deviceId}/actuators/${actuatorId}/test-ack`,
        {},
      );
      return true;
    } catch (error) {
      console.error('Chyba pri potvrzeni testu:', error);
      return false;
    }
  },

  /**
   * Ulozeni konfigurace aktuatoru (jmeno, oblibene, atd.)
   */
  saveConfig: async (deviceId: string, config: ActuatorConfig): Promise<boolean> => {
    try {
      await apiClient.put<{ success: boolean }>(
        `/devices/${deviceId}/actuators/${config.id}/config`,
        config as any,
      );

      // Aktualizace cache
      const actuator = actuatorStatesCache.get(config.id);
      if (actuator) {
        actuator.name = config.name;
        actuator.category = config.category;
        actuator.isFavorite = config.isFavorite;
        actuatorStatesCache.set(config.id, actuator);
      }

      return true;
    } catch (error) {
      console.error('Chyba pri ukladani konfigurace:', error);
      return false;
    }
  },

  /**
   * Ziskani oblibenych aktuatoru
   */
  getFavorites: async (deviceId: string): Promise<Actuator[]> => {
    const actuators = await actuatorService.getActuators(deviceId);
    return actuators.filter((a) => a.isFavorite);
  },

  /**
   * Nastaveni/odebreni z oblibenych
   */
  toggleFavorite: async (deviceId: string, actuatorId: string): Promise<boolean> => {
    const actuator = actuatorStatesCache.get(actuatorId);
    if (!actuator) return false;

    const newConfig: ActuatorConfig = {
      id: actuator.id,
      name: actuator.name,
      category: actuator.category,
      isFavorite: !actuator.isFavorite,
    };

    return actuatorService.saveConfig(deviceId, newConfig);
  },

  /**
   * Ziskani aktivnich (zapnutych) aktuatoru
   */
  getActiveActuators: (): Actuator[] => {
    return Array.from(actuatorStatesCache.values()).filter((a) => a.state === 'ON');
  },

  /**
   * Aktualizace stavu z WebSocket
   */
  updateFromWebSocket: (actuatorId: string, state: ActuatorState, timestamp?: Date): void => {
    const actuator = actuatorStatesCache.get(actuatorId);
    if (actuator) {
      actuator.state = state;
      actuator.lastStateChange = timestamp || new Date();
      actuatorStatesCache.set(actuatorId, actuator);
    }
  },

  /**
   * Vycisteni cache
   */
  clearCache: (): void => {
    actuatorStatesCache.clear();
    selectedDeviceId = null;
  },

  /**
   * Ziskani cache (pro debug)
   */
  getCache: (): Map<string, Actuator> => {
    return new Map(actuatorStatesCache);
  },

  /**
   * Ziskani historie prikazu pro aktuator
   */
  getCommandHistory: async (
    deviceId: string,
    actuatorId: string,
    limit: number = 50,
  ): Promise<CommandHistoryItem[]> => {
    try {
      const response = await apiClient.get<{ data: CommandHistoryItem[] }>(
        `/devices/${deviceId}/actuators/${actuatorId}/history`,
        { params: { limit } },
      );
      return response.data || [];
    } catch (error) {
      console.warn('Nelze nacist historii prikazu:', error);
      return [];
    }
  },

  /**
   * Ziskani konfigurace aktuatoru
   */
  getActuatorConfig: async (
    deviceId: string,
    actuatorId: string,
  ): Promise<ActuatorConfig | null> => {
    try {
      const response = await apiClient.get<{ data: ActuatorConfig }>(
        `/devices/${deviceId}/actuators/${actuatorId}/config`,
      );
      return response.data || null;
    } catch (error) {
      console.warn('Nelze nacist konfiguraci aktuatoru:', error);
      return null;
    }
  },
};

// Type for command history
export interface CommandHistoryItem {
  id: number;
  deviceId: string;
  actuatorId: string;
  command: 'ON' | 'OFF';
  status: 'PENDING' | 'SENT' | 'SUCCESS' | 'FAILED' | 'TIMEOUT';
  createdAt: Date;
  executedAt?: Date;
  errorMessage?: string;
  initiatedBy?: string;
}
